import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, storage, auth, functions } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, setDoc, onSnapshot, DocumentData, collection, addDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore'; 
import { isTeamMemberEmail } from '../utils/teamAuth';
import { buildMandantNr, extractCoreFromMandantNr, isValidMonthYearCore } from '../utils/mandantNrFormat';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { 
    Upload, CheckCircle2, CreditCard, Eye, Loader2, Building2, Phone, LogOut, Lock, Send, AlertTriangle, X, Unlock, MapPin, ShieldAlert, Mail, ArrowLeft, Info
} from 'lucide-react';

const OnboardingWizard: React.FC = () => {
    const clientData = useAppStore(state => state.clientData);
    const setClientData = useAppStore(state => state.setClientData); 
    const updateSettings = useAppStore(state => state.updateSettings); 
    const team = useAppStore(state => state.team);
    const teamRegistry = useAppStore(state => state.teamRegistry || []);
    
    // 💉 SCHLÜSSEL ZUM GOD-MODE & CRM DATEN
    const { impersonatedClientId, crmClients, pendingApprovals } = useAdminStore();
    const isGodMode = !!impersonatedClientId;
    
    // Wir suchen uns die CRM-Daten vom Mandanten
    const targetClient = useMemo(() => {
        if (!isGodMode) return null;
        const allClients = [...(crmClients || []), ...(pendingApprovals || [])];
        return allClients.find(c => c.id === impersonatedClientId) || null;
    }, [isGodMode, impersonatedClientId, crmClients, pendingApprovals]);
    
    const [userData, setUserData] = useState<DocumentData | null>(null);
    const [formData, setFormData] = useState({ 
        vorname: '', nachname: '', firmenname: '', email: '', rechtsform: 'Einzelunternehmen',
        strasse: '', plz: '', ort: '', telefon: '', salonFestnetz: '', branchName: '', land: 'DE', umsatzsteuerbefreit: false
    });
    
    // 💉 ANTI-GEISTER-WISCHER: Schützt deine Eingaben davor, beim Upload überschrieben zu werden
    const formInitialized = useRef(false);
    /** PEBase-Identität: Name/E-Mail aus Auth-Pool fixiert (1 Mensch = 1 E-Mail = 1 UID) */
    const [identityLocked, setIdentityLocked] = useState(false);

    // F2: Tarif & Vertrag für Direkt-Login-Nutzer (Akte ohne Registrierungsdaten, z. B. PEBase)
    const [tarifWahl, setTarifWahl] = useState<'Solo' | 'TeamPro' | ''>('');
    const [wizIsBestandskunde, setWizIsBestandskunde] = useState<boolean | null>(null);
    const [wizMandantNr, setWizMandantNr] = useState('');
    const [wizZugangAb, setWizZugangAb] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [wizContractAccepted, setWizContractAccepted] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isStripeLoading, setIsStripeLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);

    const isCoachAccount = !isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry);

    useEffect(() => {
        if (isCoachAccount) {
            window.location.href = '/';
        }
    }, [isCoachAccount]);
    
    useEffect(() => {
        const safeId = (impersonatedClientId === "undefined" || impersonatedClientId === "null") ? null : impersonatedClientId;
        const targetUid = safeId || auth.currentUser?.uid;
        if (!targetUid) return;
        if (!isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) {
            window.location.href = '/';
            return;
        }

        const unsub = onSnapshot(doc(db, 'kalkulation_mandanten', targetUid), async (docSnap) => {
            
            const tClient = (targetClient || {}) as Record<string, any>;
            const dClient = tClient.clientData || {};

            let kVorname = tClient.vorname || dClient.vorname || '';
            let kNachname = tClient.nachname || dClient.nachname || '';
            let kEmail = dClient.mailadresse || tClient.email || (!isGodMode ? (auth.currentUser?.email || '') : '') || '';
            let kTelefon = dClient.whatsappNr || dClient.telefon || tClient.telefon || tClient.whatsappNr || '';
            let kSalonFestnetz = String(tClient.salonFestnetz || dClient.salonFestnetz || tClient.festnetz || dClient.festnetz || '').trim();
            const unternehmerNameStr = dClient.unternehmername || '';

            // Reparatur: Falls der Admin den Namen in der Schublade zusammengefasst hat ("Susi Sorglos")
            if (!kVorname && !kNachname && unternehmerNameStr) {
                const parts = unternehmerNameStr.split(' ');
                kVorname = parts[0] || '';
                kNachname = parts.slice(1).join(' ') || '';
            } else if (!kVorname && kNachname && kNachname.includes(' ')) {
                const parts = kNachname.split(' ');
                kVorname = parts[0] || '';
                kNachname = parts.slice(1).join(' ') || '';
            }

            if (!isGodMode) {
                const nameParts = auth.currentUser?.displayName ? auth.currentUser.displayName.split(' ') : [];
                if (!kVorname) kVorname = nameParts[0] || '';
                if (!kNachname) kNachname = nameParts.slice(1).join(' ') || '';
                // Fallback: Registrierungsdaten aus users/{uid} (AuthGateway schreibt dorthin)
                if (!kVorname || !kNachname || !kTelefon || !kEmail) {
                    try {
                        const userSnap = await getDoc(doc(db, 'users', targetUid));
                        if (userSnap.exists()) {
                            const u = userSnap.data() || {};
                            if (!kVorname) kVorname = String(u.firstName || '').trim();
                            if (!kNachname) kNachname = String(u.lastName || '').trim();
                            if (!kTelefon) kTelefon = String(u.phone || '').trim();
                            if (!kEmail) kEmail = String(u.email || '').trim();
                        }
                    } catch (e) {
                        console.warn("Onboarding: users-Fallback fehlgeschlagen", e);
                    }
                }
                // PEBase-Profil (gleiche UID / Auth-Pool) — Prefill + Identität fixieren
                try {
                    const peSnap = await getDoc(doc(db, 'pebase_users', targetUid));
                    if (peSnap.exists()) {
                        const p = peSnap.data() || {};
                        if (!kVorname) kVorname = String(p.firstName || p.vorname || '').trim();
                        if (!kNachname) kNachname = String(p.lastName || p.nachname || '').trim();
                        if (!kTelefon) kTelefon = String(p.phone || p.telefon || p.whatsappNr || '').trim();
                        if (!kEmail) kEmail = String(p.email || p.mail || '').trim();
                        if ((!kVorname || !kNachname) && typeof p.name === 'string' && p.name.trim()) {
                            const parts = p.name.trim().split(/\s+/);
                            if (!kVorname) kVorname = parts[0] || '';
                            if (!kNachname) kNachname = parts.slice(1).join(' ') || '';
                        }
                        setIdentityLocked(true);
                    }
                } catch (e) {
                    console.warn("Onboarding: pebase_users-Fallback fehlgeschlagen", e);
                }
                // Auth-E-Mail fest an UID binden (1 Mensch = 1 E-Mail = 1 UID)
                const authEmail = String(auth.currentUser?.email || '').trim();
                if (authEmail) kEmail = authEmail;
            }

            const kFirma = dClient.salonname || tClient.firmenname || '';
            const resolvedEmail = kEmail || '';
            const resolvedTelefon = kTelefon || '';
            const kLand = tClient.land || dClient.land || 'DE';
            const kUst = tClient.umsatzsteuerbefreit ?? dClient.umsatzsteuerbefreit ?? false;
            
            const kStrasse = dClient.strasse || tClient.strasse || '';
            const kPlz = dClient.plz || tClient.plz || '';
            const kOrt = dClient.ort || tClient.ort || '';

            if (!docSnap.exists()) {
                if (!isGodMode) {
                    if (isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) {
                        window.location.href = '/';
                        return;
                    }
                    await setDoc(doc(db, 'kalkulation_mandanten', targetUid), {
                        vorname: kVorname,
                        nachname: kNachname,
                        email: resolvedEmail,
                        telefon: resolvedTelefon,
                        whatsappNr: resolvedTelefon,
                        salonFestnetz: kSalonFestnetz,
                        isUnlockedByAdmin: false,
                        onboardingStatus: 'started',
                        land: kLand,
                        umsatzsteuerbefreit: kUst,
                        'settings.selectedCountryId': kLand,
                        'clientData.vorname': kVorname,
                        'clientData.nachname': kNachname,
                        'clientData.email': resolvedEmail,
                        'clientData.mailadresse': resolvedEmail,
                        'clientData.telefon': resolvedTelefon,
                        'clientData.whatsappNr': resolvedTelefon,
                        'clientData.salonFestnetz': kSalonFestnetz,
                    }, { merge: true });
                    if (!formInitialized.current) {
                        setFormData(prev => ({
                            ...prev,
                            vorname: kVorname,
                            nachname: kNachname,
                            email: resolvedEmail,
                            telefon: resolvedTelefon,
                            salonFestnetz: kSalonFestnetz,
                            land: kLand,
                            umsatzsteuerbefreit: kUst,
                        }));
                        formInitialized.current = true;
                    }
                    return;
                } else {
                    setUserData({ onboardingStatus: 'started' }); 
                    
                    if (!formInitialized.current) {
                        setFormData(prev => ({
                            ...prev,
                            vorname: kVorname,
                            nachname: kNachname,
                            firmenname: kFirma,
                            email: resolvedEmail,
                            telefon: resolvedTelefon,
                            salonFestnetz: kSalonFestnetz,
                            land: kLand,
                            umsatzsteuerbefreit: kUst,
                            strasse: kStrasse, 
                            plz: kPlz,        
                            ort: kOrt          
                        }));
                        formInitialized.current = true;
                    }
                }
            } else {
                const dbData = docSnap.data() || {};
                setUserData(dbData);
                
                // 💉 CHIRURGIE: Verhindert, dass der Echtzeit-Sync deine Eingaben beim Upload überschreibt!
                if (!formInitialized.current) {
                    const dbClientData = dbData.clientData || {};
                    setFormData(prev => ({
                        vorname: dbData.vorname || dbClientData.vorname || kVorname,
                        nachname: dbData.nachname || dbClientData.nachname || kNachname,
                        firmenname: dbData.firmenname || dbClientData.salonname || dbClientData.firmenname || kFirma,
                        email: dbData.email || dbClientData.mailadresse || dbClientData.email || resolvedEmail,
                        rechtsform: dbData.rechtsform || dbData.unternehmensform || dbClientData.unternehmensform || 'Einzelunternehmen',
                        strasse: dbData.strasse || dbClientData.strasse || kStrasse,
                        plz: dbData.plz || dbClientData.plz || kPlz,
                        ort: dbData.ort || dbClientData.ort || kOrt,
                        telefon: dbData.whatsappNr || dbClientData.whatsappNr || dbData.telefon || dbClientData.telefon || resolvedTelefon,
                        salonFestnetz: String(dbData.salonFestnetz || dbClientData.salonFestnetz || dbData.festnetz || dbClientData.festnetz || kSalonFestnetz || '').trim(),
                        branchName: String(dbData.branchName || dbClientData.branchName || '').trim(),
                        land: dbData.land || dbClientData.land || kLand,
                        umsatzsteuerbefreit: dbData.umsatzsteuerbefreit ?? dbClientData.umsatzsteuerbefreit ?? kUst
                    }));
                    formInitialized.current = true;
                }
            }
        }, (error) => {
            console.error("FATAL: Listener abgestürzt, Loop verhindert:", error);
        });
        
        return () => unsub();
    }, [impersonatedClientId, isGodMode, targetClient, team, teamRegistry]);

    const handleSaveDraft = async (showNotification = true) => {
        if (!isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) return;
        setIsSaving(true);
        try {
            const targetUid = impersonatedClientId || auth.currentUser?.uid;
            if (targetUid) {
                // Überschreibschutz: Tarif-/Billing-Root-Felder nie aus Stammdaten-Draft anfassen
                const {
                    tarifStatus: _ts,
                    packageTier: _pt,
                    customerNumber: _cn,
                    billingStatus: _bs,
                    ...safeFormData
                } = formData as typeof formData & Record<string, unknown>;
                await setDoc(doc(db, 'kalkulation_mandanten', targetUid), { ...safeFormData }, { merge: true });
                if (showNotification) alert("Daten erfolgreich gespeichert!");
            }
        } catch (err) {
            console.error("Speicherfehler:", err);
            alert("Fehler beim Speichern der Daten.");
        } finally {
            setIsSaving(false);
        }
    };

    // Autosave: Stammdaten ~1,5 s nach letzter Eingabe still in die Akte schreiben
    // (schützt Firmenname & Co. vor Reload-Verlust; formInitialized blockiert den Prefill-Lauf)
    useEffect(() => {
        if (!formInitialized.current) return;
        if (userData?.onboardingStatus === 'submitted') return;
        const timer = setTimeout(() => {
            void handleSaveDraft(false);
        }, 1500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- bewusst nur formData; handleSaveDraft schließt formData ein
    }, [formData]);

    const handleLogout = async () => {
        if (!isGodMode) {
            await handleSaveDraft(false);
            auth.signOut();
        } else {
            window.location.href = '/';
        }
    };

    const handleStripePayment = async () => {
        setIsStripeLoading(true);
        try {
            const createSession = httpsCallable(functions, 'createStripeSetupSession');
            const result: any = await createSession({
                package: clientData?.selectedPackage || 'basic',
                successUrl: window.location.origin + '/onboarding?payment=success',
                cancelUrl: window.location.origin + '/onboarding?payment=cancel'
            });

            if (result.data?.url) {
                window.location.href = result.data.url;
            }
        } catch (err) {
            console.error("Stripe Fehler:", err);
            const targetUid = impersonatedClientId || auth.currentUser?.uid;
            if (targetUid) {
                if (!isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) return;
                await updateDoc(doc(db, 'kalkulation_mandanten', targetUid), { stripeSetupComplete: true, onboardingStep: 4 });
            }
        } finally {
            setIsStripeLoading(false);
        }
    };

    const resolveOnboardingYear = (data?: DocumentData | null) => {
        const src = data || userData;
        const fromReg = Number(src?.registrationYear) || 0;
        return String(Math.max(2027, fromReg || new Date().getFullYear()));
    };

    const persistBwaToYearAkte = async (
        targetUid: string,
        finalUrls: string[],
        yearStr: string
    ) => {
        const docRef = doc(db, 'kalkulation_mandanten', targetUid);
        const freshSnap = await getDoc(docRef);
        const freshData = freshSnap.exists() ? freshSnap.data() : {};
        const currentYears: any[] =
            freshData?.clientData?.geschaeftsjahre ||
            freshData?.geschaeftsjahre ||
            [];

        let updatedJahre: any[];
        if (currentYears.some((j: any) => String(j.jahr) === yearStr)) {
            updatedJahre = currentYears.map((j: any) =>
                String(j.jahr) === yearStr
                    ? { ...j, bwaUrl: finalUrls[0] || '', bwaUrls: finalUrls, status: j.status || 'datenerfassung' }
                    : j
            );
        } else {
            updatedJahre = [
                ...currentYears,
                {
                    jahr: Number(yearStr),
                    status: 'datenerfassung',
                    bwaUrl: finalUrls[0] || '',
                    bwaUrls: finalUrls,
                },
            ];
        }

        await setDoc(
            docRef,
            {
                bwaUploaded: finalUrls.length > 0,
                bwaUrl: finalUrls[0] || '',
                bwaUrls: finalUrls,
                'clientData.bwaUrl': finalUrls[0] || '',
                'clientData.bwaUrls': finalUrls,
                'clientData.geschaeftsjahre': updatedJahre,
                geschaeftsjahre: updatedJahre,
            },
            { merge: true }
        );

        await setDoc(
            doc(db, 'kalkulation_mandanten', targetUid, 'jahre', yearStr),
            {
                bwaUrl: finalUrls[0] || '',
                bwaUrls: finalUrls,
                'clientData.bwaUrl': finalUrls[0] || '',
                'clientData.bwaUrls': finalUrls,
            },
            { merge: true }
        );

        if (typeof setClientData === 'function') {
            setClientData((prev: any) => ({
                ...(prev || {}),
                bwaUrl: finalUrls[0] || '',
                bwaUrls: finalUrls,
                geschaeftsjahre: updatedJahre,
            }));
        }

        return updatedJahre;
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const allValid = files.every(f => f.type === 'application/pdf');
        if (!allValid) {
            alert("Fehler! Bitte lade ausschließlich originale PDF-Dokumente hoch. Keine Fotos.");
            if (e.target) e.target.value = '';
            return;
        }

        setIsUploading(true);
        try {
            if (!isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) return;
            const targetUid = impersonatedClientId || auth.currentUser?.uid;
            if (!targetUid) throw new Error("Nicht eingeloggt / Keine UID");

            const yearStr = resolveOnboardingYear();
            const existingBwaUrls = userData?.bwaUrls || (userData?.bwaUrl ? [userData.bwaUrl] : []);
            const newUrls: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const timestamp = Date.now();
                const fileRef = ref(storage, `kalkulation_mandanten/${targetUid}/uploads/BWA_${yearStr}_Onboarding_${timestamp}_${i}.pdf`);
                const metadata = { contentType: 'application/pdf' };
                const snapshot = await uploadBytes(fileRef, file, metadata);
                const downloadUrl = await getDownloadURL(snapshot.ref);
                newUrls.push(downloadUrl);
            }

            const finalUrls = [...existingBwaUrls, ...newUrls];
            await persistBwaToYearAkte(targetUid, finalUrls, yearStr);

            setUserData((prev: any) => ({
                ...(prev || {}),
                bwaUploaded: true,
                bwaUrl: finalUrls[0],
                bwaUrls: finalUrls,
            }));

            alert(`${files.length} Datei(en) erfolgreich hochgeladen!`);
        } catch (err) {
            console.error("Upload Fehler:", err);
            alert("Fehler beim Upload. Bitte versuche es erneut.");
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleSubmitFinal = async () => {
        // F2-Schranke: keine Minimal-Akte ohne Tarif & Vertrag einreichen (Direkt-Login-Pfad)
        let wizardMandantNr = '';
        let wizardProvidedOldId = '';
        let wizardVertragsbeginn = '';
        let wizardTarifFields: Record<string, any> = {};
        if (needsTarifVertrag) {
            if (wizIsBestandskunde === null) {
                alert("Bitte gib an, ob du bereits PreisExpert-Mandant bist.");
                return;
            }
            if (!tarifWahl) {
                alert("Bitte wähle deinen Tarif (Solo oder Team).");
                return;
            }
            if (!wizContractAccepted) {
                alert("Bitte akzeptiere die AGB und bestätige die Kenntnisnahme der Datenschutzerklärung.");
                return;
            }
            const nowCore = `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
            if (wizIsBestandskunde === true) {
                const raw = String(wizMandantNr || '').trim().replace(/\s+/g, ' ');
                if (!raw) {
                    alert("Bitte gib deine Mandant-Nummer ein.");
                    return;
                }
                wizardProvidedOldId = raw;
                // Alt (PEO/MA-I) und Neu (MA - …): nur Core weiterverwenden (wie Registrierung)
                let core = extractCoreFromMandantNr(raw);
                if (!core || !/\d/.test(core)) {
                    core = raw.toUpperCase();
                    core = core.replace(/^(MA-I|MA-B|PEO-T-|PEO-S-|PEO-B-|PEO-T|PEO-S|PEO-B)\s*/, '');
                    core = core.replace(/[- ]+[A-Z]$/, '');
                    core = core.replace(/^[-\s]+|[-\s]+$/g, '');
                    core = core.replace(/(\d{2})\.(\d{4})/g, '$1/$2');
                    core = core.trim();
                }
                if (!core || !/\d/.test(core)) {
                    alert("Bitte gib eine gültige Mandant-Nummer ein (z.B. MA-I 22/2025).");
                    return;
                }
                wizardMandantNr = buildMandantNr({ core, coachInitial: null, tarifStatus: tarifWahl });
                // Bestandsmandant: Tool-Start = Einstiegsmonat (heute), NICHT der Kern der alten Nr
                wizardVertragsbeginn = nowCore;
            } else {
                const [zj, zm] = (wizZugangAb || '').split('-');
                const zugangCore = zj && zm && isValidMonthYearCore(`${zm}/${zj}`) ? `${zm}/${zj}` : nowCore;
                wizardMandantNr = buildMandantNr({ core: zugangCore, coachInitial: null, tarifStatus: tarifWahl });
                wizardVertragsbeginn = zugangCore;
            }
            const isSoloWahl = tarifWahl === 'Solo';
            wizardTarifFields = {
                tarifStatus: tarifWahl,
                packageTier: tarifWahl,
                isSolo: isSoloWahl,
                hasEmployeeEval: !isSoloWahl,
                isTeamProPlus: false,
                insiderTeamleaderPlusChecked: false,
                insiderTeamleaderStandardChecked: !isSoloWahl,
                insiderSoloChecked: isSoloWahl,
                isBestandskunde: wizIsBestandskunde === true,
                vertragsbeginn: wizardVertragsbeginn,
                registrationYear: String(Math.max(2027, new Date().getFullYear())),
                initialContractAccepted: true,
                contractAcceptedAt: serverTimestamp(),
                'clientData.tarifStatus': tarifWahl,
                'clientData.packageTier': tarifWahl,
                'clientData.isSolo': isSoloWahl,
                'clientData.hasEmployeeEval': !isSoloWahl,
                'clientData.isTeamProPlus': false,
                'clientData.insiderTeamleaderPlusChecked': false,
                'clientData.insiderTeamleaderStandardChecked': !isSoloWahl,
                'clientData.insiderSoloChecked': isSoloWahl,
                'clientData.vertragsbeginn': wizardVertragsbeginn,
            };
        }

        if (!window.confirm("Sind alle Angaben korrekt? Nach dem Einreichen kannst du die Daten nicht mehr ändern, bis dein Betreuer sie geprüft hat.")) {
            return;
        }
        
        setIsSubmitting(true);
        try {
            if (!isGodMode && isTeamMemberEmail(auth.currentUser?.email, team, teamRegistry)) return;
            const targetUid = impersonatedClientId || auth.currentUser?.uid;
            if (targetUid) {
                // Mandantennummer aus Registrierung absichern (Martina-Fix)
                // F2: Fallback auf die im Wizard erhobene Nummer (Direkt-Login ohne Registrierungsdaten)
                const resolvedMandantNr = String(
                    userData?.mandantNr ||
                    userData?.clientData?.mandantNr ||
                    wizardMandantNr ||
                    userData?.providedOldId ||
                    ''
                ).trim();
                const resolvedProvidedOldId = String(userData?.providedOldId || wizardProvidedOldId || '').trim();

                // Dual-Write: Top-Level + clientData-Felder per Dot-Notation
                // (Firestore merge ersetzt sonst die gesamte clientData-Map und löscht Bestand)
                // Überschreibschutz: Registrierungs-Root-Felder nur bei aktivem 1b-Zweig setzen
                const {
                    tarifStatus: _omitTs,
                    packageTier: _omitPt,
                    customerNumber: _omitCn,
                    billingStatus: _omitBs,
                    ...safeSubmitForm
                } = formData as typeof formData & Record<string, unknown>;
                await setDoc(doc(db, 'kalkulation_mandanten', targetUid), {
                    ...safeSubmitForm,
                    ...wizardTarifFields,
                    vorname: formData.vorname,
                    nachname: formData.nachname,
                    firmenname: formData.firmenname,
                    email: formData.email,
                    isUnlockedByAdmin: false,
                    isReadyForReview: true,
                    onboardingStatus: 'submitted',
                    ...(resolvedMandantNr ? {
                        mandantNr: resolvedMandantNr,
                        'clientData.mandantNr': resolvedMandantNr,
                    } : {}),
                    ...(resolvedProvidedOldId ? { providedOldId: resolvedProvidedOldId } : {}),
                    // Premium-Tools: Default gesperrt, bestehende Käufe aus userData nicht überschreiben
                    unlockedPremiumTools: Array.isArray(userData?.unlockedPremiumTools)
                        ? userData.unlockedPremiumTools
                        : (Array.isArray(userData?.clientData?.unlockedPremiumTools)
                            ? userData.clientData.unlockedPremiumTools
                            : []),
                    'clientData.unlockedPremiumTools': Array.isArray(userData?.unlockedPremiumTools)
                        ? userData.unlockedPremiumTools
                        : (Array.isArray(userData?.clientData?.unlockedPremiumTools)
                            ? userData.clientData.unlockedPremiumTools
                            : []),
                    'settings.selectedCountryId': formData.land,
                    'clientData.vorname': formData.vorname,
                    'clientData.nachname': formData.nachname,
                    'clientData.firmenname': formData.firmenname,
                    'clientData.salonname': formData.firmenname,
                    'clientData.email': formData.email,
                    'clientData.mailadresse': formData.email,
                    'clientData.strasse': formData.strasse,
                    'clientData.plz': formData.plz,
                    'clientData.ort': formData.ort,
                    'clientData.telefon': formData.telefon,
                    'clientData.whatsappNr': formData.telefon,
                    'clientData.salonFestnetz': formData.salonFestnetz,
                    'clientData.branchName': formData.branchName,
                    'clientData.land': formData.land,
                    'clientData.umsatzsteuerbefreit': formData.umsatzsteuerbefreit,
                    'clientData.unternehmensform': formData.rechtsform,
                    salonFestnetz: formData.salonFestnetz,
                    branchName: formData.branchName,
                    whatsappNr: formData.telefon,
                }, { merge: true });

                // Akte 2027 automatisch anlegen — BWA aus Onboarding-Upload mitnehmen (nicht leeren!)
                const yearStr = resolveOnboardingYear(userData);
                const yearNum = Number(yearStr);
                const preservedBwaUrls: string[] = Array.isArray(userData?.bwaUrls)
                    ? userData.bwaUrls.filter((u: any) => typeof u === 'string' && u.trim())
                    : (userData?.bwaUrl ? [String(userData.bwaUrl)] : []);
                const preservedMain = preservedBwaUrls[0] || '';

                const defaultJahre = [{
                    jahr: yearNum,
                    status: 'datenerfassung',
                    bwaUrl: preservedMain,
                    bwaUrls: preservedBwaUrls,
                }];
                const mandantDocRef = doc(db, 'kalkulation_mandanten', targetUid);
                await updateDoc(mandantDocRef, {
                    'clientData.geschaeftsjahre': defaultJahre,
                    geschaeftsjahre: defaultJahre,
                    bwaUploaded: preservedBwaUrls.length > 0,
                    bwaUrl: preservedMain,
                    bwaUrls: preservedBwaUrls,
                    'clientData.bwaUrl': preservedMain,
                    'clientData.bwaUrls': preservedBwaUrls,
                });
                await setDoc(
                    doc(db, 'kalkulation_mandanten', targetUid, 'jahre', yearStr),
                    {
                        bwaUrl: preservedMain,
                        bwaUrls: preservedBwaUrls,
                        clientData: {
                            bwaUrl: preservedMain,
                            bwaUrls: preservedBwaUrls,
                        },
                    },
                    { merge: true }
                );

                // Admin-Mail erst NACH erfolgreichem Persistieren
                try {
                    await addDoc(collection(db, 'mail_queue'), {
                        email: 'erfolg@friseur-coaching.de',
                        type: 'onboarding_submitted',
                        subject: 'Neuer Onboarding-Bewerber im Freigabe-Center',
                        participantName: `${formData.vorname} ${formData.nachname}`.trim(),
                        salonName: formData.firmenname,
                        applicantEmail: formData.email,
                        applicantUid: targetUid,
                        appBaseUrl: window.location.origin,
                        status: 'pending',
                        createdAt: serverTimestamp(),
                    });
                } catch (mailErr) {
                    console.error("Onboarding mail_queue Trigger fehlgeschlagen:", mailErr);
                }
                
                if (updateSettings) updateSettings('selectedCountryId', formData.land);
                setClientData((prev: any) => ({
                    ...prev,
                    ...(needsTarifVertrag ? { tarifStatus: tarifWahl, vertragsbeginn: wizardVertragsbeginn } : {}),
                    land: formData.land,
                    umsatzsteuerbefreit: formData.umsatzsteuerbefreit,
                    vorname: formData.vorname,
                    nachname: formData.nachname,
                    email: formData.email,
                    firmenname: formData.firmenname,
                    salonname: formData.firmenname,
                    mailadresse: formData.email,
                    telefon: formData.telefon,
                    whatsappNr: formData.telefon,
                    salonFestnetz: formData.salonFestnetz,
                    branchName: formData.branchName,
                    ...(resolvedMandantNr ? { mandantNr: resolvedMandantNr } : {}),
                    bwaUrl: preservedMain,
                    bwaUrls: preservedBwaUrls,
                    geschaeftsjahre: defaultJahre,
                    isUnlockedByAdmin: false,
                    isReadyForReview: true,
                    onboardingStatus: 'submitted',
                }));
                try {
                    localStorage.setItem('CURRENT_YEAR', yearStr);
                    localStorage.setItem('CURRENT_YEAR_STATUS', 'datenerfassung');
                    sessionStorage.setItem('CURRENT_YEAR', yearStr);
                    sessionStorage.setItem('CURRENT_YEAR_STATUS', 'datenerfassung');
                } catch (_) { /* ignore */ }
                try {
                    const { migrateLocalStateToCloud } = await import('../config/firebaseSync');
                    await migrateLocalStateToCloud(true);
                } catch (syncErr) {
                    console.error('Onboarding: Cloud-Sync nach Akten-Anlage fehlgeschlagen:', syncErr);
                }
                // Warteseite erzwingen: Snapshot + lokale Flags
                setUserData((prev: any) => ({
                    ...(prev || {}),
                    ...formData,
                    ...(needsTarifVertrag ? {
                        tarifStatus: tarifWahl,
                        isBestandskunde: wizIsBestandskunde === true,
                        vertragsbeginn: wizardVertragsbeginn,
                        initialContractAccepted: true,
                    } : {}),
                    ...(resolvedMandantNr ? { mandantNr: resolvedMandantNr } : {}),
                    ...(resolvedProvidedOldId ? { providedOldId: resolvedProvidedOldId } : {}),
                    isUnlockedByAdmin: false,
                    isReadyForReview: true,
                    onboardingStatus: 'submitted',
                }));
                if (!window.location.pathname.includes('/onboarding')) {
                    window.location.replace('/onboarding');
                }
            }
        } catch (err) {
            console.error("Fehler beim Einreichen:", err);
            alert("Fehler beim Übermitteln.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const isStammdatenComplete = !!(formData.vorname && formData.nachname && formData.email && formData.firmenname && formData.strasse && formData.plz && formData.ort && formData.telefon && formData.land);
    const isPaymentComplete = !!userData?.stripeSetupComplete || (userData?.onboardingStep && userData.onboardingStep >= 4); 
    const isBwaComplete = !!userData?.bwaUploaded;

    // F2: Akte ohne Tarif aus der Registrierung (Direkt-Login, z. B. reiner PEBase-Nutzer)
    // → Tarifwahl war Pflicht; Schritt 1b ist permanent deaktiviert (HARD-OVERRIDE)
    // (früher: hasRegistrationTarif = !!(userData?.tarifStatus || userData?.clientData?.tarifStatus))
    const needsTarifVertrag = false;
    const isTarifVertragComplete = !needsTarifVertrag || (
        !!tarifWahl
        && wizContractAccepted
        && wizIsBestandskunde !== null
        && (wizIsBestandskunde === false || !!wizMandantNr.trim())
        && (wizIsBestandskunde === true || /^\d{4}-\d{2}$/.test(wizZugangAb))
    );

    // 💉 CHIRURGIE: BWA aus der Sperr-Bedingung entfernt!
    const isAllComplete = (isStammdatenComplete && isPaymentComplete && isTarifVertragComplete);
    
    const isSubmitted = userData?.onboardingStatus === 'submitted';
    // Freigabe-Screen NUR wenn Admin freigeschaltet HAT und Onboarding wirklich eingereicht wurde
    const isUnlocked = userData?.isUnlockedByAdmin === true && isSubmitted;

    // Auto-Heal: Ghost-Unlock zurücksetzen, solange noch nicht submitted
    useEffect(() => {
        if (isGodMode) return;
        if (!userData) return;
        if (userData.onboardingStatus === 'submitted') return;
        if (userData.isUnlockedByAdmin !== true) return;
        const targetUid = impersonatedClientId || auth.currentUser?.uid;
        if (!targetUid) return;

        setUserData((prev: any) => ({ ...(prev || {}), isUnlockedByAdmin: false }));
        updateDoc(doc(db, 'kalkulation_mandanten', targetUid), {
            isUnlockedByAdmin: false,
            onboardingStatus: userData.onboardingStatus || 'started',
        }).catch((e) => {
            console.warn("Onboarding Auto-Heal Unlock-Reset fehlgeschlagen:", e);
        });
    }, [isGodMode, userData, impersonatedClientId]);

    const activeLogo = clientData?.selectedPackage === 'basic' ? '/assets/logo_alt.png' : '/assets/logo.png';
    const uploadedBwaCount = userData?.bwaUrls?.length || (userData?.bwaUrl ? 1 : 0);

    if (isUnlocked && !isGodMode) {
        return (
            <div className="min-h-screen bg-[#020617] text-slate-200 font-sans flex flex-col items-center justify-center p-4">
                <div className="max-w-xl w-full bg-[#0a0f1c] border border-slate-800 rounded-3xl p-10 text-center shadow-2xl animate-fadeIn">
                    <div className="w-24 h-24 bg-[#E6C299]/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-[#E6C299]/30">
                        <Unlock className="w-12 h-12 text-[#E6C299]" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">Account freigeschaltet!</h1>
                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                        Dein Betreuer hat deine Daten erfolgreich geprüft und dein Setup abgeschlossen. 
                        Du kannst nun dein Dashboard betreten.
                    </p>
                    <button 
                        onClick={() => {
                            // Durchbricht Redirect-Schleife: Freigabe-Screen sieht Firestore-true,
                            // App-Store hatte oft noch false → sofort wieder /onboarding.
                            try {
                                sessionStorage.setItem('PE_ADMIN_UNLOCKED', '1');
                            } catch (_) { /* ignore */ }
                            try {
                                useAppStore.setState((s: any) => ({
                                    clientData: { ...(s.clientData || {}), isUnlockedByAdmin: true },
                                    isInitialized: true,
                                }));
                            } catch (_) { /* ignore */ }
                            window.location.replace('/');
                        }} 
                        className="flex items-center justify-center gap-2 px-10 py-4 bg-[#E6C299] hover:bg-[#d4b08c] text-black rounded-xl mx-auto transition-colors font-bold tracking-wider uppercase text-sm shadow-[0_0_30px_rgba(230,194,153,0.4)]"
                    >
                        Zum Dashboard wechseln <Send className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </div>
        );
    }

    if (isSubmitted && !isGodMode) {
        return (
            <div className="min-h-screen bg-[#020617] text-slate-200 font-sans flex flex-col items-center justify-center p-4">
                <div className="max-w-xl w-full bg-[#0a0f1c] border border-slate-800 rounded-3xl p-10 text-center shadow-2xl animate-fadeIn">
                    <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-green-500/30">
                        <CheckCircle2 className="w-12 h-12 text-green-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">Akte übergeben!</h1>
                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                        Vielen Dank! Deine Daten, die BWA und die Zahlungsanbindung wurden erfolgreich an deinen persönlichen Betreuer übermittelt. 
                        <br/><br/>
                        Wir prüfen nun alles und richten deine Kostenstruktur ein. Sobald dein Dashboard freigeschaltet ist, erhältst du eine Nachricht von uns.
                    </p>
                    <button onClick={handleLogout} className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl mx-auto transition-colors font-bold tracking-wider uppercase text-sm">
                        <LogOut className="w-4 h-4" /> Jetzt Ausloggen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {showPdfModal && (
                <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fadeIn">
                    <div className="bg-[#0f1524] border border-slate-700 rounded-2xl w-full max-w-5xl h-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-900/50">
                            <h3 className="text-white font-bold flex items-center gap-2">
                                <Eye className="w-5 h-5 text-[#E6C299]"/> Muster BWA Vorschau
                            </h3>
                            <button onClick={() => setShowPdfModal(false)} className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg p-2 transition-colors flex items-center gap-2 text-sm font-bold">
                                <X className="w-4 h-4" /> Schließen
                            </button>
                        </div>
                        <div className="flex-1 w-full bg-slate-800">
                            <iframe src="/assets/muster-bwa.pdf" className="w-full h-full border-0" title="BWA Muster" />
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-[#020617] text-slate-200 font-sans flex flex-col items-center">
                <header className="w-full border-b border-slate-800 bg-[#020617]/80 backdrop-blur-md sticky top-0 z-40">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 h-32 flex items-center justify-between">
                        <div className="flex items-center">
                            <img src={activeLogo} alt="PreisExpert Logo" className="h-24 object-contain" />
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="hidden md:flex items-center gap-2 px-4 py-1.5 bg-slate-900 border border-slate-800 rounded-full">
                                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Account Setup</span>
                            </div>
                            <button onClick={handleLogout} className="p-2 text-slate-500 hover:text-white transition-colors" title={isGodMode ? 'Zurück zum Dashboard' : 'Ausloggen'}>
                                {isGodMode ? <ArrowLeft className="w-5 h-5" /> : <LogOut className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </header>

                <main className="w-full max-w-4xl px-4 md:px-8 py-12 space-y-8 animate-fadeIn">
                    
                    {isGodMode && (
                        <div className="bg-red-900/20 border border-red-500/50 p-6 rounded-2xl shadow-[0_0_20px_rgba(220,38,38,0.2)] flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="text-center md:text-left">
                                <h2 className="text-xl font-black text-white tracking-widest uppercase mb-1 flex items-center justify-center md:justify-start gap-2">
                                    <ShieldAlert className="w-6 h-6 text-red-500" /> Admin Override Aktiv
                                </h2>
                                <p className="text-slate-400 text-sm">Du betrachtest die Onboarding-Ansicht für: <strong className="text-white">{targetClient?.firmenname || targetClient?.vorname || targetClient?.firstName || 'Mandant'}</strong></p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                                <button 
                                    onClick={() => window.open('/', '_blank')} 
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-900/80 hover:bg-slate-800 text-slate-300 border border-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md w-full sm:w-auto whitespace-nowrap"
                                    title="Tipp: Am besten im Inkognito-Fenster öffnen, falls du direkt ins Dashboard geleitet wirst."
                                >
                                    <Eye className="w-4 h-4" /> Auth-Gateway prüfen
                                </button>
                                <button 
                                    onClick={handleLogout}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-red-950/80 hover:bg-red-900 text-red-200 border border-red-500/50 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(220,38,38,0.3)] hover:scale-[1.02] w-full sm:w-auto whitespace-nowrap"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Ansicht verlassen
                                </button>
                            </div>
                        </div>
                    )}

                    {!isGodMode && (
                        <div className="text-center space-y-4 mb-12">
                            <h2 className="text-4xl font-bold text-white tracking-tight">Herzlich Willkommen!</h2>
                            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed text-lg">
                                Wir freuen uns darauf, deine Kalkulation auf das nächste Level zu heben. 
                                Bitte vervollständige dein Profil, damit wir deinen persönlichen Account final einrichten können.
                            </p>
                        </div>
                    )}

                    <div className="bg-[#0a0f1c] border border-slate-800 rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 border-b border-slate-800 pb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-[#E6C299]" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">1. Stammdaten</h2>
                                    <p className="text-sm text-slate-500">Persönliche & geschäftliche Details</p>
                                </div>
                            </div>
                            {isStammdatenComplete ? (
                                <span className="flex items-center gap-2 bg-green-500/10 text-green-400 px-4 py-2 rounded-full text-sm font-bold border border-green-500/20">
                                    <CheckCircle2 className="w-4 h-4" /> Vollständig
                                </span>
                            ) : (
                                <span className="flex items-center gap-2 bg-yellow-500/10 text-yellow-500 px-4 py-2 rounded-full text-sm font-bold border border-yellow-500/20">
                                    <Lock className="w-4 h-4" /> Felder offen
                                </span>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2 bg-[#E6C299]/5 border border-[#E6C299]/20 p-6 rounded-2xl mb-2">
                                <label className="block text-xs font-bold text-[#E6C299] mb-3 uppercase tracking-wider flex items-center gap-2">
                                    <MapPin className="w-4 h-4" /> Sitz des Unternehmens / Kalkulations-Basis *
                                </label>
                                <select 
                                    value={formData.land} 
                                    onChange={(e) => {
                                        const newLand = e.target.value;
                                        setFormData({...formData, land: newLand});
                                        if (updateSettings) updateSettings('selectedCountryId', newLand);
                                        setClientData((prev: any) => ({ ...prev, land: newLand }));
                                    }} 
                                    className="w-full bg-slate-900 border border-[#E6C299]/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors appearance-none font-bold"
                                >
                                    <option value="DE">Deutschland (DE)</option>
                                    <option value="AT">Österreich (AT)</option>
                                    <option value="CH">Schweiz (CH)</option>
                                    <option value="IT">Italien / Südtirol (IT)</option>
                                </select>
                                
                                <label className="flex items-start cursor-pointer group mt-6">
                                    <div className="flex items-center h-6">
                                        <input 
                                            type="checkbox" 
                                            checked={formData.umsatzsteuerbefreit} 
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setFormData({...formData, umsatzsteuerbefreit: isChecked});
                                                setClientData((prev: any) => ({ ...prev, umsatzsteuerbefreit: isChecked }));
                                            }} 
                                            className="w-5 h-5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-slate-900" 
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <span className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors">Ich bin Umsatzsteuerbefreit</span>
                                        <p className="text-xs text-slate-500 mt-1">Gilt z.B. für Kleinunternehmer oder medizinische Heilberufe.</p>
                                    </div>
                                </label>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Vorname *</label>
                                <input
                                    type="text"
                                    value={formData.vorname}
                                    onChange={(e) => { if (!identityLocked) setFormData({...formData, vorname: e.target.value}); }}
                                    readOnly={identityLocked}
                                    className={`w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors ${identityLocked ? 'opacity-80 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Nachname *</label>
                                <input
                                    type="text"
                                    value={formData.nachname}
                                    onChange={(e) => { if (!identityLocked) setFormData({...formData, nachname: e.target.value}); }}
                                    readOnly={identityLocked}
                                    className={`w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors ${identityLocked ? 'opacity-80 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">E-Mail Adresse *</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => { if (!identityLocked) setFormData({...formData, email: e.target.value}); }}
                                        readOnly={identityLocked || !!auth.currentUser?.email}
                                        className={`w-full bg-slate-900 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors ${(identityLocked || !!auth.currentUser?.email) ? 'opacity-80 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                                {identityLocked && (
                                    <p className="mt-1 text-[11px] text-slate-500">Aus deinem PreisExpert-Konto (PEBase) übernommen — an diese UID gebunden.</p>
                                )}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Firmenname / Salon *</label>
                                <input type="text" value={formData.firmenname} onChange={(e) => setFormData({...formData, firmenname: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Straße & Hausnummer *</label>
                                <input type="text" value={formData.strasse} onChange={(e) => setFormData({...formData, strasse: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">PLZ *</label>
                                <input type="text" value={formData.plz} onChange={(e) => setFormData({...formData, plz: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Ort *</label>
                                <input type="text" value={formData.ort} onChange={(e) => setFormData({...formData, ort: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Rechtsform *</label>
                                <select value={formData.rechtsform} onChange={(e) => setFormData({...formData, rechtsform: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors appearance-none">
                                    <option value="Einzelunternehmen">Einzelunternehmen</option>
                                    <option value="GmbH">GmbH</option>
                                    <option value="UG">UG (haftungsbeschränkt)</option>
                                    <option value="GbR">GbR</option>
                                    <option value="Andere">Andere</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Salonnummer (Festnetz)</label>
                                <input
                                    type="text"
                                    value={formData.salonFestnetz}
                                    onChange={(e) => setFormData({...formData, salonFestnetz: e.target.value})}
                                    placeholder="z.B. 040 / 123 456 78"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors"
                                />
                                <p className="text-[10px] text-slate-500 mt-1">Festnetznummer deines Salons — nicht die WhatsApp-/Handynummer und keine PE-Mandant-Nummer.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Filialname / Standortbezeichnung</label>
                                <input
                                    type="text"
                                    value={formData.branchName}
                                    onChange={(e) => setFormData({...formData, branchName: e.target.value})}
                                    placeholder="z. B. Salon City oder Filiale Nord"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors"
                                />
                                <p className="text-[10px] text-slate-500 mt-1">Optional — Bezeichnung dieses Standorts / dieser Filiale.</p>
                            </div>
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">
                                    WhatsApp-Nummer *
                                    <span
                                        className="inline-flex cursor-help text-[#E6C299]"
                                        data-tooltip-id="onboarding-tooltip"
                                        data-tooltip-content="Bitte unbedingt die WhatsApp-Nummer eintragen, über die wir dich persönlich erreichen."
                                    >
                                        <Info className="w-3.5 h-3.5" />
                                    </span>
                                </label>
                                <div
                                    className="relative cursor-help"
                                    data-tooltip-id="onboarding-tooltip"
                                    data-tooltip-content="Bitte unbedingt die WhatsApp-Nummer eintragen, über die wir dich persönlich erreichen."
                                >
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                                    <input type="tel" value={formData.telefon} onChange={(e) => setFormData({...formData, telefon: e.target.value})} placeholder="+49 170 1234567" className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* F2: Tarif & Vertrag — nur für Direkt-Login-Nutzer ohne Registrierungsdaten (z. B. PEBase) */}
                    {needsTarifVertrag && (
                        <div className="bg-[#0a0f1c] border border-slate-800 rounded-3xl p-6 md:p-8 shadow-xl">
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 border-b border-slate-800 pb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                                        <Lock className="w-6 h-6 text-[#E6C299]" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-white">1b. Tarif & Vertrag</h2>
                                        <p className="text-sm text-slate-500">Dein PreisExpert-Konto (z.&nbsp;B. aus PEBase) wurde erkannt — bitte vervollständige deine P1-Vertragsdaten.</p>
                                    </div>
                                </div>
                                {isTarifVertragComplete ? (
                                    <span className="flex items-center gap-2 bg-green-500/10 text-green-400 px-4 py-2 rounded-full text-sm font-bold border border-green-500/20">
                                        <CheckCircle2 className="w-4 h-4" /> Vollständig
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2 bg-yellow-500/10 text-yellow-500 px-4 py-2 rounded-full text-sm font-bold border border-yellow-500/20">
                                        <Lock className="w-4 h-4" /> Angaben offen
                                    </span>
                                )}
                            </div>

                            <div className="space-y-8">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Bist du bereits PreisExpert-Mandant? *</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => { setWizIsBestandskunde(false); setWizMandantNr(''); }}
                                            className={`p-5 rounded-xl border-2 text-left transition-all ${wizIsBestandskunde === false ? 'bg-[#E6C299]/10 border-[#E6C299] text-white shadow-lg' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                        >
                                            <div className="font-bold text-sm uppercase tracking-wider">Ich bin neu</div>
                                            <div className="text-xs mt-1 opacity-80">Ich möchte ein neues Setup buchen.</div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setWizIsBestandskunde(true)}
                                            className={`p-5 rounded-xl border-2 text-left transition-all ${wizIsBestandskunde === true ? 'bg-blue-900/20 border-blue-500 text-white shadow-lg' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                        >
                                            <div className="font-bold text-sm uppercase tracking-wider">Bestehender Mandant</div>
                                            <div className="text-xs mt-1 opacity-80">Ich habe bereits eine Mandant-Nummer.</div>
                                        </button>
                                    </div>
                                    {wizIsBestandskunde === true && (
                                        <div className="mt-4 animate-fadeIn">
                                            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Deine Mandant-Nummer *</label>
                                            <input
                                                type="text"
                                                value={wizMandantNr}
                                                onChange={(e) => setWizMandantNr(e.target.value)}
                                                placeholder="z.B. MA-I 22/2025"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors"
                                            />
                                        </div>
                                    )}
                                    {wizIsBestandskunde === false && (
                                        <div className="mt-4 animate-fadeIn">
                                            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Zugang ab Monat *</label>
                                            <input
                                                type="month"
                                                value={wizZugangAb}
                                                onChange={(e) => setWizZugangAb(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E6C299] transition-colors"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Dein Tarif *</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {([
                                            { id: 'Solo' as const, label: 'Solo', desc: 'Einzelunternehmer ohne Mitarbeiter.' },
                                            { id: 'TeamPro' as const, label: 'Team', desc: 'Salon mit Mitarbeitern · Teamauswertung aktiv (Team-Plus automatisch ab Umsatzschwelle).' },
                                        ]).map((t) => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => setTarifWahl(t.id)}
                                                className={`p-5 rounded-xl border-2 text-left transition-all ${tarifWahl === t.id ? 'bg-[#E6C299]/10 border-[#E6C299] text-white shadow-lg' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                            >
                                                <div className="font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                                    {tarifWahl === t.id && <CheckCircle2 className="w-4 h-4 text-[#E6C299]" />}
                                                    {t.label}
                                                </div>
                                                <div className="text-xs mt-1 opacity-80">
                                                    {t.desc}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={wizContractAccepted}
                                        onChange={(e) => setWizContractAccepted(e.target.checked)}
                                        className="w-5 h-5 mt-0.5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-slate-900"
                                    />
                                    <span className="text-sm text-slate-300 group-hover:text-white transition-colors leading-relaxed">
                                        Ich akzeptiere die AGB und habe die{' '}
                                        <a
                                            href="https://www.friseur-coaching.de/datenschutzerklaerung/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline text-[#E6C299] hover:text-white"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Datenschutzerklärung
                                        </a>
                                        {' '}zur Kenntnis genommen. *
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-[#0a0f1c] border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                                        <CreditCard className="w-5 h-5 text-[#E6C299]" />
                                    </div>
                                    <h2 className="text-xl font-bold text-white">2. Zahlungsart</h2>
                                </div>
                                {isPaymentComplete && <CheckCircle2 className="w-6 h-6 text-green-500" />}
                            </div>
                            <p className="text-slate-400 text-sm mb-6 flex-1">
                                Bitte hinterlege deine bevorzugte Zahlungsart. <strong className="text-white">Es werden heute 0,00 € abgebucht.</strong> Die Daten dienen nur der späteren Rechnungsstellung für dein gewähltes Paket.
                            </p>
                            {isPaymentComplete ? (
                                <div className="bg-green-500/10 border border-green-500/30 rounded-xl py-4 text-center text-green-400 font-bold">
                                    Zahlungsprofil verknüpft
                                </div>
                            ) : (
                                <button 
                                    onClick={handleStripePayment} 
                                    disabled={isStripeLoading || isGodMode} 
                                    className={`w-full font-bold py-4 rounded-xl transition-all text-sm uppercase tracking-widest flex items-center justify-center shadow-lg border ${isGodMode ? 'bg-slate-900 text-slate-600 border-slate-800 cursor-not-allowed' : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'}`}
                                >
                                    {isGodMode ? 'Stripe als Admin gesperrt' : (isStripeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Jetzt sicher hinterlegen')}
                                </button>
                            )}
                        </div>

                        <div className="bg-[#0a0f1c] border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                                        <Upload className="w-5 h-5 text-[#E6C299]" />
                                    </div>
                                    <h2 className="text-xl font-bold text-white">3. BWA Upload</h2>
                                </div>
                                {isBwaComplete && <CheckCircle2 className="w-6 h-6 text-green-500" />}
                            </div>

                            <p className="text-slate-400 text-sm mb-4">
                                Eine vollständige BWA muss zwingend die Erfolgsrechnung und den detaillierten <strong>Wertenachweis</strong> (Auflistung der Einzelkonten) enthalten.
                            </p>

                            <div className="bg-red-950/30 border border-red-900/50 p-4 rounded-xl mb-4 flex gap-4 items-start">
                                <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
                                <div className="text-sm text-red-200/90 leading-relaxed">
                                    <strong>KEINE FOTOS!</strong><br/>
                                    Abfotografierte oder eingescannte Bilder werden abgelehnt. Bitte lade <strong>originale PDF-Dokumente</strong> hoch. Du kannst hier auch mehrere PDFs gleichzeitig markieren.
                                </div>
                            </div>
                            
                            <div className="text-slate-400 text-sm mb-4">
                                <button onClick={() => setShowPdfModal(true)} className="text-[#E6C299] hover:text-white mt-1 inline-flex items-center gap-2 transition-colors font-bold">
                                    <Eye className="w-4 h-4" /> Muster im Viewer ansehen
                                </button>
                            </div>
                            
                            {isBwaComplete ? (
                                <div className="mt-auto relative bg-green-500/10 border border-green-500/30 rounded-xl py-4 text-center text-green-400 font-bold">
                                    {uploadedBwaCount > 1 ? `${uploadedBwaCount} BWA-Dateien empfangen` : 'BWA erfolgreich empfangen'}
                                    <span className="block text-xs text-green-500/70 font-normal mt-1">(Zum Ersetzen / Erweitern hier klicken)</span>
                                    <input type="file" accept=".pdf" multiple onChange={handleFileUpload} disabled={isUploading} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                </div>
                            ) : (
                                <div className="mt-auto relative group h-20">
                                    <input type="file" accept=".pdf" multiple onChange={handleFileUpload} disabled={isUploading} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                                    <div className={`h-full border-2 border-slate-700 border-dashed group-hover:border-[#E6C299] rounded-xl transition-all flex flex-col items-center justify-center bg-slate-900/50 ${isUploading ? 'opacity-50' : ''}`}>
                                        {isUploading ? <Loader2 className="w-6 h-6 text-[#E6C299] animate-spin" /> : <Upload className="w-6 h-6 text-slate-500 group-hover:text-[#E6C299]" />}
                                        <span className="text-xs font-bold text-slate-400 group-hover:text-white mt-1">PDF(s) hochladen</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-[#0f1524] border border-slate-800 p-8 rounded-3xl shadow-2xl mt-8 flex flex-col md:flex-row items-center justify-between gap-6 border-t-2 border-t-[#E6C299]/10">
                        <button onClick={handleLogout} className="flex items-center justify-center gap-2 px-6 py-4 border border-slate-700 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-slate-800 transition-all w-full md:w-auto font-bold tracking-wider">
                            {isGodMode ? <ArrowLeft className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
                            {isGodMode ? 'Zurück zum Dashboard' : 'Zwischenspeichern & Schließen'}
                        </button>
                        <button 
                            onClick={handleSubmitFinal}
                            disabled={!isAllComplete || isSubmitting || isSaving}
                            className={`
                                flex items-center justify-center gap-3 px-10 py-4 rounded-xl font-bold uppercase tracking-widest transition-all w-full md:w-auto
                                ${isAllComplete 
                                    ? 'bg-[#E6C299] hover:bg-[#d4b08c] text-black shadow-[0_0_30px_rgba(230,194,153,0.4)]' 
                                    : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'}
                            `}
                        >
                            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            Setup abschließen
                        </button>
                    </div>
                </main>
            </div>
            <Tooltip
                id="onboarding-tooltip"
                className="z-[100000] shadow-2xl font-bold border border-[#dcb07b]"
                style={{
                    backgroundColor: '#E6C299',
                    color: '#08090b',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    maxWidth: '300px',
                }}
            />
        </>
    );
};

export default OnboardingWizard;